// src/components/MobileControls.tsx
import React from "react";
import styled from "styled-components";

const ControlsContainer = styled.div<{ isVisible: boolean }>`
  display: ${(props) => (props.isVisible ? "flex" : "none")};
  position: fixed;
  bottom: 20px;
  left: 0;
  right: 0;
  justify-content: space-between;
  padding: 0 20px;
  pointer-events: none;
  z-index: 1000;

  /* Debug: Add visible border for testing */
  border: 2px solid lime;

  @media (min-width: 768px) {
    display: none; /* Hide on desktop */
  }
`;

const PlayerControls = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  pointer-events: all;
`;

const PlayerLabel = styled.div<{ color: string }>`
  background: ${(props) => props.color};
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
`;

const ControlGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const DPad = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(3, 1fr);
  gap: 2px;
  width: 120px;
  height: 120px;
`;

const DPadButton = styled.button<{ gridArea: string; isPressed?: boolean }>`
  grid-area: ${(props) => props.gridArea};
  background: ${(props) =>
    props.isPressed
      ? "linear-gradient(145deg, #4a4a4a, #2a2a2a)"
      : "linear-gradient(145deg, #6a6a6a, #4a4a4a)"};
  border: 2px solid ${(props) => (props.isPressed ? "#333" : "#888")};
  border-radius: 8px;
  color: white;
  font-size: 16px;
  font-weight: bold;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
  user-select: none;
  touch-action: manipulation;
  transition: all 0.1s ease;

  &:active {
    background: linear-gradient(145deg, #4a4a4a, #2a2a2a);
    border-color: #333;
    transform: scale(0.95);
  }

  &:hover {
    background: linear-gradient(145deg, #7a7a7a, #5a5a5a);
  }
`;

// const JumpButton = styled.button<{ isPressed?: boolean }>`
//   width: 60px;
//   height: 60px;
//   border-radius: 50%;
//   background: ${(props) =>
//     props.isPressed
//       ? "linear-gradient(145deg, #4a4a4a, #2a2a2a)"
//       : "linear-gradient(145deg, #ff6b6b, #ee5a5a)"};
//   border: 3px solid ${(props) => (props.isPressed ? "#333" : "#fff")};
//   color: white;
//   font-size: 14px;
//   font-weight: bold;
//   text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
//   user-select: none;
//   touch-action: manipulation;
//   transition: all 0.1s ease;
//   box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);

//   &:active {
//     background: linear-gradient(145deg, #4a4a4a, #2a2a2a);
//     border-color: #333;
//     transform: scale(0.9);
//     box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
//   }
// `;

const MenuButton = styled.button`
  position: fixed;
  top: 20px;
  right: 20px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(145deg, #333, #555);
  border: 2px solid #888;
  color: white;
  font-size: 18px;
  user-select: none;
  touch-action: manipulation;
  z-index: 1001;
  pointer-events: all;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);

  &:active {
    background: linear-gradient(145deg, #222, #444);
    transform: scale(0.9);
  }

  @media (min-width: 768px) {
    display: none;
  }
`;

interface MobileControlsProps {
  onPlayerInput: (
    player: 1 | 2,
    action: "left" | "right" | "jump",
    pressed: boolean
  ) => void;
  onMenuPress: () => void;
  isVisible: boolean;
}

export const MobileControls: React.FC<MobileControlsProps> = ({
  onPlayerInput,
  onMenuPress,
  isVisible,
}) => {
  const handleButtonPress = (
    player: 1 | 2,
    action: "left" | "right" | "jump",
    pressed: boolean
  ) => {
    onPlayerInput(player, action, pressed);
  };

  const createTouchHandlers = (
    player: 1 | 2,
    action: "left" | "right" | "jump"
  ) => ({
    onTouchStart: (e: React.TouchEvent) => {
      // Don't preventDefault on touch events to avoid passive listener warning
      handleButtonPress(player, action, true);
    },
    onTouchEnd: (e: React.TouchEvent) => {
      // Don't preventDefault on touch events to avoid passive listener warning
      handleButtonPress(player, action, false);
    },
    onTouchCancel: (e: React.TouchEvent) => {
      console.log(`Touch cancel: Player ${player}, Action: ${action}`);
      // Don't preventDefault on touch events to avoid passive listener warning
      handleButtonPress(player, action, false);
    },
    // Also support mouse for testing on desktop
    onMouseDown: (e: React.MouseEvent) => {
      e.preventDefault();
      handleButtonPress(player, action, true);
    },
    onMouseUp: (e: React.MouseEvent) => {
      e.preventDefault();
      handleButtonPress(player, action, false);
    },
    onMouseLeave: (e: React.MouseEvent) => {
      e.preventDefault();
      handleButtonPress(player, action, false);
    },
  });

  return (
    <>
      <MenuButton onClick={onMenuPress}>☰</MenuButton>

      <ControlsContainer isVisible={isVisible}>
        {/* Player 1 Controls (Left side) */}
        <PlayerControls>
          <PlayerLabel color="#0099ff">P1</PlayerLabel>
          <ControlGroup>
            <DPad>
              <div></div>
              <DPadButton
                gridArea="1 / 2 / 2 / 3"
                {...createTouchHandlers(1, "jump")}
              >
                ↑
              </DPadButton>
              <div></div>

              <DPadButton
                gridArea="2 / 1 / 3 / 2"
                {...createTouchHandlers(1, "left")}
              >
                ←
              </DPadButton>
              <div></div>
              <DPadButton
                gridArea="2 / 3 / 3 / 4"
                {...createTouchHandlers(1, "right")}
              >
                →
              </DPadButton>

              <div></div>
              <div></div>
              <div></div>
            </DPad>
          </ControlGroup>
        </PlayerControls>

        {/* Player 2 Controls (Right side) */}
        <PlayerControls>
          <PlayerLabel color="#ff3300">P2</PlayerLabel>
          <ControlGroup>
            <DPad>
              <div></div>
              <div></div>

              <DPadButton
                gridArea="1 / 2 / 2 / 3"
                {...createTouchHandlers(2, "jump")}
              >
                ↑
              </DPadButton>
              <div></div>

              <DPadButton
                gridArea="2 / 1 / 3 / 2"
                {...createTouchHandlers(2, "left")}
              >
                ←
              </DPadButton>
              <div></div>
              <DPadButton
                gridArea="2 / 3 / 3 / 4"
                {...createTouchHandlers(2, "right")}
              >
                →
              </DPadButton>

              <div></div>
              {/* <JumpButton {...createTouchHandlers(2, "jump")}>JUMP</JumpButton> */}
              <div></div>
              <div></div>
            </DPad>
          </ControlGroup>
        </PlayerControls>
      </ControlsContainer>
    </>
  );
};
